import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNowPlaying } from "../context/NowPlayingContext";
import { toast } from "sonner";
import {
  ArrowLeft, Play, Pause, Lock, ListMusic, Plus, Trash2, ChevronUp, ChevronDown, Search, Save, Sparkles,
} from "lucide-react";

function TierBadge({ tier }) {
  const isPlat = (tier || "").toLowerCase() === "platinum";
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
      style={
        isPlat
          ? { background: "rgba(255,213,79,0.15)", border: "1px solid rgba(255,213,79,0.55)", color: "#FFD54F" }
          : { background: "rgba(0,150,136,0.18)", border: "1px solid rgba(0,150,136,0.55)", color: "#4DB6AC" }
      }
    >
      {(tier || "premium").toUpperCase()}
    </span>
  );
}

function AddSongsModal({ existing, allTracks, onClose, onAdd }) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(new Set());
  const existingIds = useMemo(() => new Set(existing.map((s) => s.id)), [existing]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTracks.filter(
      (t) =>
        !existingIds.has(t.id) &&
        (!q || t.title.toLowerCase().includes(q) || (t.artist || "").toLowerCase().includes(q))
    );
  }, [allTracks, existingIds, query]);

  const togglePick = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = () => {
    if (picked.size === 0) { onClose(); return; }
    onAdd(Array.from(picked));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl flex flex-col"
        style={{ background: "rgba(8,10,14,0.95)", border: "1px solid rgba(212,155,255,0.35)", maxHeight: "85vh" }}
        data-testid="add-songs-modal"
      >
        <div className="p-5 pb-3 border-b border-white/5">
          <h2 className="text-lg font-bold">Add songs</h2>
          <div className="relative mt-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-soft" />
            <input
              className="glass-input pl-9"
              placeholder="Search songs…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="add-songs-search"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {filtered.length === 0 ? (
            <p className="text-center text-sm text-muted-soft py-8">No songs to add.</p>
          ) : (
            filtered.map((t) => {
              const isPicked = picked.has(t.id);
              return (
                <button
                  key={t.id}
                  onClick={() => togglePick(t.id)}
                  className="w-full p-3 rounded-xl flex items-center gap-3 text-left transition-colors"
                  style={{
                    background: isPicked ? "rgba(76,175,80,0.15)" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${isPicked ? "rgba(76,175,80,0.5)" : "rgba(255,255,255,0.06)"}`,
                  }}
                  data-testid={`pick-${t.id}`}
                >
                  <span
                    className="w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center"
                    style={{
                      borderColor: isPicked ? "#4CAF50" : "rgba(255,255,255,0.25)",
                      background: isPicked ? "#4CAF50" : "transparent",
                    }}
                  >
                    {isPicked && <span className="text-white text-xs">✓</span>}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{t.title}</p>
                    <p className="text-[10px] text-muted-soft">{t.frequency} · {t.duration}</p>
                  </div>
                  <TierBadge tier={t.tier_required || t.subscription_tier} />
                </button>
              );
            })
          )}
        </div>
        <div className="p-4 border-t border-white/5 flex gap-2">
          <button onClick={onClose} className="btn-outline flex-1" data-testid="modal-cancel">Cancel</button>
          <button
            onClick={save}
            disabled={picked.size === 0}
            className="flex-1 px-4 py-2.5 rounded-xl font-bold disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#9C27B0,#d49bff)", color: "#fff" }}
            data-testid="modal-add"
          >
            Add {picked.size > 0 && `(${picked.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PlaylistDetailScreen() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatinum = (user?.tier || "").toLowerCase() === "platinum";

  const [name, setName] = useState("");
  const [songs, setSongs] = useState([]);
  const [allTracks, setAllTracks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const { track: npTrack, isPlaying, play } = useNowPlaying();
  const activeId = npTrack?.id;

  useEffect(() => {
    if (!isPlatinum) return;
    (async () => {
      try {
        const { data } = await api.get("/music/tracks");
        setAllTracks(data.tracks || []);
      } catch {}
      if (!isNew) {
        try {
          const { data } = await api.get(`/playlists/${id}`);
          setName(data.playlist.name);
          setSongs(data.songs || []);
        } catch (e) {
          toast.error("Playlist not found");
          navigate("/music?tab=playlists");
        }
      }
    })();
  }, [id, isNew, isPlatinum]);

  const saveName = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (isNew) {
        const { data } = await api.post("/playlists", { name: name.trim() });
        toast.success("Playlist created");
        navigate(`/music/playlists/${data.id}`, { replace: true });
      } else {
        await api.put(`/playlists/${id}`, { name: name.trim() });
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const addSongs = async (songIds) => {
    try {
      await api.post(`/playlists/${id}/songs`, { song_ids: songIds });
      const { data } = await api.get(`/playlists/${id}`);
      setSongs(data.songs || []);
      setShowAdd(false);
      toast.success(`Added ${songIds.length} song${songIds.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const removeSong = async (songId) => {
    if (!window.confirm("Remove this song from the playlist?")) return;
    try {
      await api.delete(`/playlists/${id}/songs/${songId}`);
      setSongs((prev) => prev.filter((s) => s.id !== songId));
      toast.success("Removed");
    } catch { toast.error("Failed"); }
  };

  const move = async (idx, delta) => {
    const next = [...songs];
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setSongs(next);
    try {
      await api.put(`/playlists/${id}/songs/reorder`, { song_ids: next.map((s) => s.id) });
    } catch {
      toast.error("Reorder failed");
      setSongs(songs); // rollback
    }
  };

  if (!isPlatinum) {
    return (
      <Shell section="music">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#4CAF50] text-sm mb-3 hover:underline">
          <ArrowLeft size={14} /> Back
        </button>
        <div
          className="rounded-2xl p-7 text-center"
          style={{ background: "rgba(8,14,10,0.72)", border: "1px solid rgba(255,213,79,0.45)" }}
        >
          <Lock size={32} className="mx-auto text-[#FFD54F] mb-3" />
          <h2 className="text-xl font-bold mb-1">Playlists are Platinum-only</h2>
          <p className="text-soft text-sm mb-5">Upgrade to create personal playlists.</p>
          <button onClick={() => navigate("/subscription")} className="btn-amber inline-flex items-center gap-2">
            <Sparkles size={14} /> Upgrade to Platinum
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell section="music">
      <button
        onClick={() => navigate("/music")}
        className="flex items-center gap-1 text-[#4CAF50] text-sm mb-3 hover:underline"
        data-testid="back-to-music"
      >
        <ArrowLeft size={14} /> Music
      </button>

      <div
        className="rounded-2xl p-5 mb-5 fade-up"
        style={{ background: "rgba(8,14,10,0.72)", border: "1px solid rgba(212,155,255,0.30)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}
        data-testid="playlist-header"
      >
        <div className="flex items-start gap-4">
          <div
            className="w-16 h-16 rounded-xl flex-shrink-0 flex items-center justify-center"
            style={{ background: "rgba(123,31,162,0.25)", border: "1px solid rgba(212,155,255,0.35)" }}
          >
            <ListMusic size={28} className="text-[#d49bff]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-muted-soft">
              {isNew ? "Create playlist" : "Playlist"} · {songs.length} song{songs.length === 1 ? "" : "s"}
            </p>
            <input
              className="bg-transparent border-0 outline-none text-2xl font-extrabold text-white w-full mt-1 focus:bg-white/5 rounded-lg px-1.5 -mx-1.5"
              placeholder="Playlist name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
              data-testid="playlist-name-input"
            />
          </div>
          <button
            onClick={saveName}
            disabled={busy || !name.trim()}
            className="px-4 py-2 rounded-xl font-bold text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#9C27B0,#d49bff)", color: "#fff" }}
            data-testid="save-playlist"
          >
            <Save size={14} /> {isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>

      {!isNew && (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] uppercase tracking-wider text-muted-soft">Songs in this playlist</p>
            <button
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-1.5 text-sm font-bold px-3.5 py-1.5 rounded-xl"
              style={{ background: "rgba(123,31,162,0.25)", color: "#d49bff", border: "1px solid rgba(212,155,255,0.4)" }}
              data-testid="add-songs-btn"
            >
              <Plus size={14} /> Add songs
            </button>
          </div>

          {songs.length === 0 ? (
            <div className="glass-card-strong p-10 text-center fade-up">
              <ListMusic size={32} className="mx-auto text-[#d49bff] mb-3" />
              <p className="text-soft text-sm">This playlist is empty. Tap <b>Add songs</b> to start.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="playlist-songs">
              {songs.map((s, idx) => {
                const isActive = activeId === s.id && isPlaying;
                const isLocked = s.access_level === "preview";
                return (
                  <div
                    key={s.id}
                    className="flex items-center gap-3 p-3 rounded-xl"
                    style={{ background: isActive ? "rgba(76,175,80,0.10)" : "rgba(8,14,10,0.55)", border: `1px solid ${isActive ? "rgba(76,175,80,0.45)" : "rgba(255,255,255,0.06)"}` }}
                    data-testid={`pl-song-${s.id}`}
                  >
                    <span className="text-xs font-bold text-muted-soft w-5">{idx + 1}</span>
                    <button
                      onClick={() => play(s)}
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: isActive ? "linear-gradient(135deg,#4CAF50,#66BB6A)" : "rgba(76,175,80,0.18)",
                        border: `1px solid ${isActive ? "rgba(76,175,80,0.7)" : "rgba(76,175,80,0.35)"}`,
                      }}
                      data-testid={`pl-play-${s.id}`}
                    >
                      {isActive ? <Pause size={14} className="text-white" /> : <Play size={14} className="text-[#4CAF50] ml-0.5" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold truncate">{s.title}</p>
                        <TierBadge tier={s.tier_required || s.subscription_tier} />
                        {isLocked && <Lock size={11} className="text-[#FFD54F]" />}
                      </div>
                      <p className="text-[11px] text-muted-soft">{s.frequency} · {s.duration}</p>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => move(idx, -1)} disabled={idx === 0} className="w-7 h-5 rounded hover:bg-white/5 disabled:opacity-30 inline-flex items-center justify-center" data-testid={`up-${s.id}`}>
                        <ChevronUp size={14} />
                      </button>
                      <button onClick={() => move(idx, 1)} disabled={idx === songs.length - 1} className="w-7 h-5 rounded hover:bg-white/5 disabled:opacity-30 inline-flex items-center justify-center" data-testid={`down-${s.id}`}>
                        <ChevronDown size={14} />
                      </button>
                    </div>
                    <button onClick={() => removeSong(s.id)} className="w-8 h-8 rounded-full hover:bg-red-500/10 inline-flex items-center justify-center" title="Remove from playlist" data-testid={`remove-${s.id}`}>
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {showAdd && (
            <AddSongsModal
              existing={songs}
              allTracks={allTracks}
              onClose={() => setShowAdd(false)}
              onAdd={addSongs}
            />
          )}
        </>
      )}
    </Shell>
  );
}
